/**
 * The order a case table is read in. Section 3 of the request of
 * 13 September 2026.
 *
 * Named presets rather than a column and a direction, because the orders the
 * floor actually asks for are not columns. "Urgent first" is urgent, then
 * rework, then the due date: three fields, in the client's priority, which is
 * the same priority `kitStatus` already writes down. A column is still a column
 * and the table still sorts by one; this is the other half of the same state.
 *
 * Pure comparators here, in the domain, so both halves of a case table and the
 * tests agree on what each preset means; the words in the select live in the
 * strings module, where every word does.
 */
import { ORIGIN_FIRST, originRank, type Origin } from './origin';

/**
 * What a row must carry to be ordered by these.
 *
 * Optional where a row may not have it: a job order that has left the lab has
 * no `minutesInStage` (null, since the request of 11 October 2026 put its first
 * tray's on the order; before that an order carried none), and a caller without
 * seeIdentity receives no doctor name at all. Neither is a reason for a sort to
 * throw, so both fall to the end of their order instead.
 */
export interface SortableCase {
  jobOrderNo: string;
  urgent?: boolean;
  daysLeft?: number;
  rework?: boolean;
  dentistName?: string | null;
  minutesInStage?: number | null;
  /** Local, international or online. Section 2 of the request of 24 September 2026. */
  origin?: Origin;
}

/**
 * One named order a case table can be read in.
 *
 * `compare` for a table the screen already holds in memory, `sortParam` for one
 * the server orders. A preset with neither leaves the rows exactly as they
 * arrived, which is how a list the server already orders keeps the order it has
 * today as its default.
 */
export interface SortPreset<Row> {
  key: string;
  label: string;
  compare?: (a: Row, b: Row) => number;
  /** The `sort` query parameter, leading minus for descending. */
  sortParam?: string;
}

/** A column the reader clicked, the other half of the same state. */
export interface ColumnSort {
  key: string;
  direction: 'asc' | 'desc';
}

export const CASE_SORT_KEYS = [
  'dueSoonest',
  'urgentFirst',
  'newestFirst',
  'oldestFirst',
  'longestWaiting',
  'doctorAtoZ',
  // Section 2 of the request of 24 September 2026: one origin's cases first.
  ORIGIN_FIRST.local,
  ORIGIN_FIRST.international,
  ORIGIN_FIRST.online,
] as const;

export type CaseSortKey = (typeof CASE_SORT_KEYS)[number];

/**
 * The presets a paged list asks the SERVER to order by, as the `sort`
 * parameter. The origin sorts, section 2 of the request of 24 September 2026:
 * the orders list and the stage lists are paged, and a page ordered in the
 * browser puts one origin first on page one and again on page two. Newest
 * first, the default of every list since section 2 of the request of
 * 5 October 2026, for the same reason: the newest case in the lab has to be at
 * the top of page one, not at the top of whichever page it landed on. The
 * other presets order the page they are given, as they always have.
 */
export const CASE_SORT_PARAM: Partial<Record<CaseSortKey, string>> = {
  newestFirst: '-createdAt',
  oldestFirst: 'createdAt',
  localFirst: ORIGIN_FIRST.local,
  internationalFirst: ORIGIN_FIRST.international,
  onlineFirst: ORIGIN_FIRST.online,
};

/**
 * The number inside a job order number.
 *
 * Numbers are issued in order and identify one case for the life of the lab, so
 * the newest case is the highest number. Compared as a NUMBER rather than as
 * text: JO-999 sorts above JO-1000 as text, which would put a case from last
 * year at the top of "Newest first" the first time the lab passed a thousand.
 */
function serialOf(jobOrderNo: string): number {
  // `Number('')` is 0, so a number with no digits in it needs no branch of its own.
  return Number(jobOrderNo.replace(/\D+/g, ''));
}

/** Blanks last, whichever direction the rest of the order runs in. */
function byName(a: string | null | undefined, b: string | null | undefined): number {
  const left = a ?? '';
  const right = b ?? '';
  if (left === '' && right === '') return 0;
  if (left === '') return 1;
  if (right === '') return -1;
  return left.localeCompare(right);
}

const flag = (value: boolean | undefined) => (value === true ? 1 : 0);

/** A row with no due date is not due: it sorts behind every row that has one. */
const days = (row: SortableCase) => row.daysLeft ?? Number.MAX_SAFE_INTEGER;

/**
 * The client's phrase, and what the floor means by it: the red-dot cases at the
 * top, then the ones that have already cost the lab twice, then the ones about
 * to go red.
 */
function urgentFirst(a: SortableCase, b: SortableCase): number {
  return (
    flag(b.urgent) - flag(a.urgent) ||
    flag(b.rework) - flag(a.rework) ||
    days(a) - days(b) ||
    serialOf(a.jobOrderNo) - serialOf(b.jobOrderNo)
  );
}

/**
 * One origin's cases first, then the other two in the client's order, and
 * urgent first inside each group, which is how the orders list and a bench both
 * read. The API sorts the same way, so a page ordered here keeps the server's
 * order. Section 2 of the request of 24 September 2026.
 */
function originFirst(first: Origin): (a: SortableCase, b: SortableCase) => number {
  return (a, b) => originRank(a.origin, first) - originRank(b.origin, first) || urgentFirst(a, b);
}

/**
 * The comparators, by preset key. Every one of them is a total order: it falls
 * back to the job order number so that two rows alike on every field it reads
 * still come out in a stable, repeatable order rather than in whatever order
 * the list happened to arrive in.
 */
export const CASE_SORT: Record<CaseSortKey, (a: SortableCase, b: SortableCase) => number> = {
  /** Due soonest, then urgent. What every floor list shows today. */
  dueSoonest: (a, b) =>
    days(a) - days(b) ||
    flag(b.urgent) - flag(a.urgent) ||
    serialOf(a.jobOrderNo) - serialOf(b.jobOrderNo),

  urgentFirst,

  /** Most recently taken in first, by the number the lab issued it. */
  newestFirst: (a, b) => serialOf(b.jobOrderNo) - serialOf(a.jobOrderNo),

  /**
   * The other way round, for a queue rather than a list: the case that has been
   * waiting longest is the one to chase. The shipping screen's default, section
   * 8.1 of the request of 16 September 2026.
   */
  oldestFirst: (a, b) => serialOf(a.jobOrderNo) - serialOf(b.jobOrderNo),

  /** Longest at this bench first. An order carries no bench time and sorts last. */
  longestWaiting: (a, b) =>
    (b.minutesInStage ?? -1) - (a.minutesInStage ?? -1) ||
    serialOf(a.jobOrderNo) - serialOf(b.jobOrderNo),

  /** By doctor, blanks last, which is where a caller without seeIdentity finds every row. */
  doctorAtoZ: (a, b) =>
    byName(a.dentistName, b.dentistName) || serialOf(a.jobOrderNo) - serialOf(b.jobOrderNo),

  localFirst: originFirst('local'),
  internationalFirst: originFirst('international'),
  onlineFirst: originFirst('online'),
};
